import { addDays, format, isSameDay } from 'date-fns';
import { ErrorMessage, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FaAnchor, FaCalendarAlt, FaCheck, FaCheckCircle, FaClock, FaExclamationTriangle, FaInfoCircle, FaUsers } from 'react-icons/fa';
import * as Yup from 'yup';
import { useAvailability } from '../hooks/useAvailability';
import { heroBluefinDeck } from '../assets/images/hero/imageAssets';
import SEOHead from '../components/SEOHead';
import { getOgImageUrl, OG_IMAGE_DIMENSIONS } from '../utils/seoSchemas';
import { ApiError, BookingType, CharterType, createBooking } from '../utils/apiService';
import { trackBookingConversion } from '../utils/analytics';
import {
  BookingForm,
  BookingLayout,
  BookingSection,
  CalendarContainer,
  CharterOption,
  CharterOptions,
  CloseButton,
  ContentContainer,
  ErrorText,
  FieldGroup,
  Label,
  Legend,
  LegendColor,
  LegendItem,
  ModalContent,
  ModalOverlay,
  OptionDetails,
  OptionTitle,
  PageContainer,
  SectionContent,
  SectionTitle,
  StyledField,
  StyledSelect,
  SubmitButton,
  Subtitle,
  SuccessIcon,
  SuccessMessage,
  Title,
  WarningIcon
} from './styles/BookingPage.styles';

interface BookingFormValues {
  bookingType: BookingType;
  charterType: CharterType;
  date: Date | null;
  name: string;
  email: string;
  phone: string;
  spotsRequested: string;
  howDidYouHear: string;
  message: string;
}

const charterOptions = [
  {
    id: 'half-day' as CharterType,
    title: 'Half-Day Adventure',
    duration: '4-6 hours',
    maxGuests: 6,
    price: '$1,799 / $300 per person',
    description: 'Perfect for beginners or those with limited time'
  },
  {
    id: 'full-day' as CharterType,
    title: 'Full-Day Charter',
    duration: '8-10 hours',
    maxGuests: 6,
    price: '$2,999 / $499 per person',
    description: 'Our most popular charter — more time for the big catch'
  },
  {
    id: 'specialty' as CharterType,
    title: 'Specialty Charter',
    duration: '8-12 hours',
    maxGuests: 6,
    price: '$3,499',
    description: 'Targeting specific species with specialized techniques'
  },
  {
    id: 'custom' as CharterType,
    title: 'Custom Private Charter',
    duration: 'Flexible',
    maxGuests: 6,
    price: 'Contact for pricing',
    description: 'Tailored experience for special occasions'
  }
];

type ModalState = 'hidden' | 'success' | 'warning';

const BookingPage = () => {
  const [modalState, setModalState] = useState<ModalState>('hidden');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { blockedDates, dateDetails, loading: availLoading, error: availError, refetch } = useAvailability();

  const isDateBlocked = (date: Date) =>
    blockedDates.some(blocked => isSameDay(blocked, date));

  const getSpotsForDate = (date: Date | null): { taken: number; remaining: number } | null => {
    if (!date) return null;
    const key = format(date, 'yyyy-MM-dd');
    const detail = dateDetails[key];
    if (!detail) return null;
    return { taken: detail.open_party_spots_taken, remaining: detail.open_party_spots_remaining };
  };

  const validationSchema = Yup.object({
    bookingType: Yup.string().required(),
    charterType: Yup.string().required('Please select a charter type'),
    date: Yup.date().required('Please select a date').nullable(),
    name: Yup.string().required('Please enter your name'),
    email: Yup.string().email('Invalid email address').required('Please enter your email'),
    phone: Yup.string().required('Please enter your phone number'),
    spotsRequested: Yup.string()
      .required('Please select number of spots')
      .notOneOf([''], 'Please select number of spots'),
    howDidYouHear: Yup.string(),
    message: Yup.string()
  });

  const handleSubmit = async (
    values: BookingFormValues,
    { setSubmitting, resetForm }: any
  ) => {
    setSubmitError(null);

    if (!values.date) {
      setSubmitError('Please select a date.');
      setSubmitting(false);
      return;
    }

    try {
      const response = await createBooking({
        customer_name: values.name,
        customer_email: values.email,
        customer_phone: values.phone,
        booking_type: values.bookingType,
        charter_type: values.charterType,
        booking_date: format(values.date, "yyyy-MM-dd") + "T07:00:00",
        spots_requested: parseInt(values.spotsRequested, 10),
        how_did_you_hear: values.howDidYouHear || undefined,
        message: values.message || undefined,
      });

      trackBookingConversion(String(response.id), values.bookingType, values.charterType);

      if (response.email_sent) {
        setModalState('success');
      } else {
        setModalState('warning');
      }
      refetch();

      resetForm({
        values: {
          bookingType: 'open_party',
          charterType: 'half-day',
          date: null,
          name: '',
          email: '',
          phone: '',
          spotsRequested: '',
          howDidYouHear: '',
          message: ''
        }
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setSubmitError(err.detail);
        } else if (err.status === 422) {
          setSubmitError('Please check your form — some information appears invalid.');
        } else {
          setSubmitError('There was a problem submitting your booking. Please try again or call us at (714) 200-9815.');
        }
      } else {
        setSubmitError('Could not reach the booking system. Please call us at (714) 200-9815.');
      }
    }

    setSubmitting(false);
  };

  // Render the spot count badge below the calendar for the selected date
  const renderSpotInfo = (selectedDate: Date | null, isOpenParty: boolean) => {
    if (!selectedDate || !isOpenParty) return null;
    const spotInfo = getSpotsForDate(selectedDate);
    const remaining = spotInfo ? spotInfo.remaining : 6;

    let color: string;
    if (remaining <= 2) {
      color = '#c0392b'; // red — almost full
    } else if (remaining <= 4) {
      color = '#d4a017'; // yellow — filling up
    } else {
      color = '#27ae60'; // green — plenty of room
    }

    return (
      <p style={{ fontSize: '0.9rem', color, marginTop: 8, fontWeight: 600, textAlign: 'center' }}>
        {remaining === 0
          ? '⛔ No spots remaining on this date'
          : `✅ ${remaining} of 6 spots available — ${format(selectedDate, 'MMMM d, yyyy')}`}
      </p>
    );
  };

  return (
    <>
      <SEOHead
        title="Book Your Charter — Fussaki Spearfishing | San Pedro, CA"
        description="Check availability and book your spearfishing or sportfishing charter with Captain Christos. Open party and private charter options available."
        canonical="https://fussaki.com/book"
        ogImage={getOgImageUrl(heroBluefinDeck)}
        ogImageWidth={OG_IMAGE_DIMENSIONS.heroBluefinDeck.width}
        ogImageHeight={OG_IMAGE_DIMENSIONS.heroBluefinDeck.height}
      />
      <PageContainer>
      <ContentContainer>
        <Title>Book Your Charter</Title>
        <Subtitle>
          Select from our available charter options and book your fishing adventure today!
        </Subtitle>

        {modalState !== 'hidden' && (
          <ModalOverlay onClick={() => setModalState('hidden')}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              {modalState === 'success' ? (
                <SuccessMessage>
                  <SuccessIcon>{FaCheckCircle({ size: 24 })}</SuccessIcon>
                  <h3>Booking Request Submitted!</h3>
                  <p>Thank you! We'll contact you within 24 hours to confirm your reservation.</p>
                  <CloseButton onClick={() => setModalState('hidden')}>Close</CloseButton>
                </SuccessMessage>
              ) : (
                <SuccessMessage>
                  <WarningIcon>{FaExclamationTriangle({ size: 24 })}</WarningIcon>
                  <h3>Booking Saved</h3>
                  <p>
                    Your booking has been saved, but the confirmation email could not be sent.
                    We'll still contact you within 24 hours. If you don't hear from us, call{' '}
                    <a href="tel:7142009815">(714) 200-9815</a>.
                  </p>
                  <CloseButton onClick={() => setModalState('hidden')}>Close</CloseButton>
                </SuccessMessage>
              )}
            </ModalContent>
          </ModalOverlay>
        )}

        <Formik
          initialValues={{
            bookingType: 'open_party' as BookingType,
            charterType: 'half-day' as CharterType,
            date: null as Date | null,
            name: '',
            email: '',
            phone: '',
            spotsRequested: '',
            howDidYouHear: '',
            message: ''
          }}
          validateOnChange={true}
          validateOnBlur={true}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setFieldValue, values }: FormikProps<BookingFormValues>) => {
            const spotInfo = getSpotsForDate(values.date);
            const isOpenParty = values.bookingType === 'open_party';
            const maxSpots = isOpenParty
              ? (spotInfo ? spotInfo.remaining : 6)
              : 8;

            return (
              <BookingLayout>
                {/* LEFT SIDE — Single unified calendar */}
                <BookingSection>
                  <SectionTitle>{FaCalendarAlt({ size: 20 })} Select a Date</SectionTitle>
                  <SectionContent>
                    {availLoading && <p style={{ color: '#666', fontSize: '0.85rem' }}>Loading availability…</p>}
                    {availError && <p style={{ color: '#e07000', fontSize: '0.85rem' }}>{availError}</p>}
                    <CalendarContainer>
                      <DatePicker
                        inline
                        selected={values.date}
                        onChange={(date: Date | null) => {
                          setFieldValue('date', date);
                          setFieldValue('spotsRequested', '');
                        }}
                        minDate={new Date()}
                        maxDate={addDays(new Date(), 90)}
                        filterDate={date => !isDateBlocked(date)}
                        calendarClassName="custom-calendar"
                      />
                    </CalendarContainer>

                    {/* Spot info for selected date */}
                    {renderSpotInfo(values.date, isOpenParty)}

                    {values.date && !isOpenParty && (
                      <p style={{ fontSize: '0.9rem', color: '#2980b9', marginTop: 8, fontWeight: 600, textAlign: 'center' }}>
                        🔒 Private Charter — {format(values.date, 'MMMM d, yyyy')}
                      </p>
                    )}

                    <Legend>
                      <LegendItem><LegendColor $bgColor="#fff" /> Available</LegendItem>
                      <LegendItem><LegendColor $bgColor="var(--accent-color)" /> Selected</LegendItem>
                      <LegendItem><LegendColor $bgColor="#f0f0f0" /> Booked</LegendItem>
                    </Legend>
                  </SectionContent>
                </BookingSection>

                {/* RIGHT SIDE — Booking form */}
                <BookingSection>
                  <SectionTitle>{FaAnchor({ size: 20 })} Booking Details</SectionTitle>
                  <SectionContent>
                    <BookingForm>

                      {/* Booking Type */}
                      <FieldGroup>
                        <Label>{FaUsers({ size: 16 })} Trip Type</Label>
                        <CharterOptions>
                          <CharterOption
                            $selected={values.bookingType === 'open_party'}
                            onClick={() => {
                              setFieldValue('bookingType', 'open_party');
                              setFieldValue('spotsRequested', '');
                            }}
                          >
                            <OptionTitle>Open Party Trip</OptionTitle>
                            <OptionDetails>
                              <div>{FaUsers({ size: 14 })} Shared boat — book individual spots</div>
                              <div>Up to 6 spots per day</div>
                            </OptionDetails>
                          </CharterOption>
                          <CharterOption
                            $selected={values.bookingType === 'private_charter'}
                            onClick={() => {
                              setFieldValue('bookingType', 'private_charter');
                              setFieldValue('spotsRequested', '');
                            }}
                          >
                            <OptionTitle>Private Charter</OptionTitle>
                            <OptionDetails>
                              <div>{FaAnchor({ size: 14 })} Whole boat — just your group</div>
                              <div>Up to 8 guests</div>
                            </OptionDetails>
                          </CharterOption>
                        </CharterOptions>
                      </FieldGroup>

                      {/* Charter Package */}
                      <FieldGroup>
                        <Label>{FaAnchor({ size: 16 })} Charter Package</Label>
                        <CharterOptions>
                          {charterOptions.map(option => (
                            <CharterOption
                              key={option.id}
                              $selected={values.charterType === option.id}
                              onClick={() => setFieldValue('charterType', option.id)}
                            >
                              <OptionTitle>{option.title}</OptionTitle>
                              <OptionDetails>
                                <div>{FaClock({ size: 14 })} {option.duration}</div>
                                <div>{FaUsers({ size: 14 })} Up to {option.maxGuests} guests</div>
                                <div>{option.price}</div>
                              </OptionDetails>
                            </CharterOption>
                          ))}
                        </CharterOptions>
                        <ErrorMessage name="charterType" component={ErrorText} />
                      </FieldGroup>

                      {/* Selected date display (read-only — driven by left calendar) */}
                      <FieldGroup>
                        <Label>{FaCalendarAlt({ size: 16 })} Selected Date</Label>
                        <StyledField
                          as="input"
                          name="dateDisplay"
                          readOnly
                          value={values.date ? format(values.date, 'MMMM d, yyyy') : ''}
                          placeholder="← Pick a date on the calendar"
                          style={{ cursor: 'default', backgroundColor: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                        />
                        <ErrorMessage name="date" component={ErrorText} />
                      </FieldGroup>

                      {/* Personal Info */}
                      <FieldGroup>
                        <Label>{FaInfoCircle({ size: 16 })} Your Information</Label>
                        <StyledField
                          name="name"
                          placeholder="Your Full Name"
                          value={values.name}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('name', e.target.value)}
                        />
                        <ErrorMessage name="name" component={ErrorText} />
                      </FieldGroup>

                      <FieldGroup>
                        <StyledField
                          name="email"
                          placeholder="Email Address"
                          type="email"
                          value={values.email}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('email', e.target.value)}
                        />
                        <ErrorMessage name="email" component={ErrorText} />
                      </FieldGroup>

                      <FieldGroup>
                        <StyledField
                          name="phone"
                          placeholder="Phone Number"
                          value={values.phone}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('phone', e.target.value)}
                        />
                        <ErrorMessage name="phone" component={ErrorText} />
                      </FieldGroup>

                      {/* Spots Requested */}
                      <FieldGroup>
                        <Label htmlFor="spots-select">
                          {FaUsers({ size: 16 })} {isOpenParty ? 'Number of Spots' : 'Group Size'}
                        </Label>
                        <StyledSelect
                          as="select"
                          name="spotsRequested"
                          id="spots-select"
                          aria-label="Spots requested"
                          value={values.spotsRequested}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFieldValue('spotsRequested', e.target.value)}
                        >
                          <option value="">
                            {isOpenParty ? 'Select number of spots' : 'Select group size'}
                          </option>
                          {Array.from({ length: maxSpots }, (_, i) => i + 1).map(n => (
                            <option key={n} value={String(n)}>
                              {n} {n === 1 ? 'Person' : 'People'}
                            </option>
                          ))}
                        </StyledSelect>
                        <ErrorMessage name="spotsRequested" component={ErrorText} />
                      </FieldGroup>

                      {/* How did you hear */}
                      <FieldGroup>
                        <Label>{FaInfoCircle({ size: 16 })} How Did You Hear About Us?</Label>
                        <StyledField
                          name="howDidYouHear"
                          placeholder="Friend, Google, Social Media, etc."
                          value={values.howDidYouHear}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('howDidYouHear', e.target.value)}
                        />
                      </FieldGroup>

                      {/* Notes */}
                      <FieldGroup>
                        <Label>Additional Notes</Label>
                        <StyledField
                          as="textarea"
                          name="message"
                          placeholder="Any special requests or additional information"
                          rows={4}
                          value={values.message}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFieldValue('message', e.target.value)}
                        />
                      </FieldGroup>

                      {/* API error */}
                      {submitError && (
                        <p style={{
                          color: '#c0392b',
                          background: '#fdf0ee',
                          border: '1px solid #e9b4ad',
                          borderRadius: 6,
                          padding: '10px 14px',
                          fontSize: '0.9rem',
                          marginBottom: 12
                        }}>
                          {submitError}
                        </p>
                      )}

                      <SubmitButton type="submit" disabled={isSubmitting || (isOpenParty && !!values.date && !!spotInfo && spotInfo.remaining === 0)}>
                        {isSubmitting ? 'Submitting…' : <>{FaCheck({ size: 16 })} Request Booking</>}
                      </SubmitButton>

                      {values.bookingType === 'private_charter' && (
                        <p style={{ fontSize: '0.8rem', color: '#888', marginTop: 8, textAlign: 'center' }}>
                          Private charter requests are reviewed within 24 hours. Your date is not held until confirmed.
                        </p>
                      )}
                    </BookingForm>
                  </SectionContent>
                </BookingSection>
              </BookingLayout>
            );
          }}
        </Formik>
      </ContentContainer>
    </PageContainer>
    </>
  );
};

export default BookingPage;
